import { PropsWithChildren } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { Components as MDXComponents } from '@mdx-js/react/lib';
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import { Document, BLOCKS, INLINES } from '@contentful/rich-text-types';
import cx from 'classnames';

import {
  RichTextBlockAssetFragment,
  RichTextBlockEntryFragment,
  RichTextHyperlinkEntryFragment,
} from './copyRendering.generated';
import checkCircleIcon from '../assets/icons/check-circle.svg';

const Paragraph: React.FC<PropsWithChildren> = ({ children }) => (
  <p className="md:text-large my-4 leading-relaxed first:mt-0 last:mb-0 whitespace-pre-line">
    {children}
  </p>
);
const H1: React.FC<PropsWithChildren> = ({ children }) => (
  <h1 className="text-4xl md:text-5xl font-semibold text-gray-800 leading-snug mt-16 mb-8 first:mt-0">
    {children}
  </h1>
);
const H2: React.FC<PropsWithChildren> = ({ children }) => (
  <h2 className="text-3xl md:text-4xl font-semibold text-gray-800 leading-snug mt-10 mb-4 first:mt-0">
    {children}
  </h2>
);
const H3: React.FC<PropsWithChildren> = ({ children }) => (
  <h3 className="text-2xl md:text-3xl font-semibold text-gray-800 leading-snug mt-8 mb-4 first:mt-0">
    {children}
  </h3>
);
const H4: React.FC<PropsWithChildren> = ({ children }) => (
  <h4 className="text-xl md:text-2xl font-semibold text-gray-800 leading-snug mt-8 my-4 first:mt-0">
    {children}
  </h4>
);
const H5: React.FC<PropsWithChildren> = ({ children }) => (
  <h5 className="text-lg md:text-xl font-semibold text-gray-800 leading-snug mt-8 my-4 first:mt-0">
    {children}
  </h5>
);
const H6: React.FC<PropsWithChildren> = ({ children }) => (
  <h6 className="md:text-xl font-semibold text-gray-800 leading-snug mt-8 my-4 first:mt-0">
    {children}
  </h6>
);
const Ul: React.FC<PropsWithChildren> = ({ children }) => (
  <ul className="list-disc my-4 last:mb-0">{children}</ul>
);
const Ol: React.FC<PropsWithChildren> = ({ children }) => (
  <ol className="list-decimal my-4 last:mb-0">{children}</ol>
);
const Li: React.FC<PropsWithChildren> = ({ children }) => (
  <li className="my-1 ml-5">{children}</li>
);
const A: React.FC<PropsWithChildren<{ href?: string }>> = ({
  children,
  href,
}) => (
  <a
    href={href}
    className="text-blue-600 hover:underline"
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);
const InternalA: React.FC<PropsWithChildren<{ href: string }>> = ({
  children,
  href,
}) => (
  <Link href={href} className="text-blue-600 hover:underline">
    {children}
  </Link>
);
const Img: React.FC<{
  src?: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
}> = (props) => (
  // eslint-disable-next-line @next/next/no-img-element
  <img {...props} alt={props.alt} className="inline" />
);

export const markdownComponents: MDXComponents = {
  h1: H1,
  h2: H2,
  h3: H3,
  h4: H4,
  h5: H5,
  h6: H6,
  ol: Ol,
  ul: Ul,
  li: Li,
  p: Paragraph,
  a: A,
  img: Img,
};

interface RichTextConfig {
  snippetListMulticolumn: boolean;
}

const richTextOptions = (links?: Links, config?: RichTextConfig): Options => ({
  renderMark: {},
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <Paragraph>{children}</Paragraph>,
    [BLOCKS.HEADING_1]: (node, children) => <H1>{children}</H1>,
    [BLOCKS.HEADING_2]: (node, children) => <H2>{children}</H2>,
    [BLOCKS.HEADING_3]: (node, children) => <H3>{children}</H3>,
    [BLOCKS.HEADING_4]: (node, children) => <H4>{children}</H4>,
    [BLOCKS.HEADING_5]: (node, children) => <H5>{children}</H5>,
    [BLOCKS.HEADING_6]: (node, children) => <H6>{children}</H6>,
    [BLOCKS.UL_LIST]: (node, children) => <Ul>{children}</Ul>,
    [BLOCKS.OL_LIST]: (node, children) => <Ol>{children}</Ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <Li>{children}</Li>,
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      const entry = links?.entries.block.find(
        (entry) => entry?.sys.id === node.data.target.sys.id,
      );

      switch (entry?.__typename) {
        case 'SnippetList':
          return (
            <ul
              className={cx('grid grid-cols-1 gap-y-2', {
                'sm:grid-cols-2': config?.snippetListMulticolumn,
              })}
            >
              {entry.items?.map(
                (item) =>
                  item && (
                    <li key={item} className="flex items-start">
                      <Image
                        src={checkCircleIcon}
                        className="mr-2 shrink-0"
                        alt="Checkmark in circle"
                      />
                      {item}
                    </li>
                  ),
              )}
            </ul>
          );
      }

      return null;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
      const asset = links?.assets.block.find(
        (asset) => asset?.sys.id === node.data.target.sys.id,
      );

      return (
        asset?.url && (
          <Img
            src={asset.url}
            alt={asset.title ?? ''}
            // Render half size for retina screens
            width={asset.width ? asset.width / 2 : undefined}
            height={asset.height ? asset.height / 2 : undefined}
          />
        )
      );
    },
    [INLINES.HYPERLINK]: (node, children) => (
      <A href={node.data.uri}>{children}</A>
    ),
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const entry = links?.entries.hyperlink.find(
        (entry) => entry?.sys.id === node.data.target.sys.id,
      );
      return (
        entry && <InternalA href={entryToLink(entry)}>{children}</InternalA>
      );
    },
  },
});

export const entryToLink = (
  entry: RichTextHyperlinkEntryFragment | null | undefined,
) => {
  switch (entry?.__typename) {
    case 'Article':
      return `/article/${entry.slug}`;
    case 'Page':
      return `/${entry.slug}`;
    case 'Agent':
      return `/agents/${entry.slug}`;
    default:
      return '';
  }
};

export interface Links {
  entries: {
    block: (RichTextBlockEntryFragment | null)[];
    hyperlink: (RichTextHyperlinkEntryFragment | null)[];
  };
  assets: {
    block: (RichTextBlockAssetFragment | null)[];
  };
}

interface RichTextProps {
  content:
    | {
        json: Document;
        links?: Links;
      }
    | null
    | undefined;
  config?: RichTextConfig;
}

export const RichText: React.FC<RichTextProps> = ({ content, config }) => (
  <>
    {content &&
      documentToReactComponents(
        content.json,
        richTextOptions(content.links, config),
      )}
  </>
);
